/**
 * Patient-friendly **unit** and **route** for every RxNorm Dose Form (DF) concept.
 *
 * The medication picker resolves an SCD/SBD product to its DF code (via the `dose_form_of`
 * terminology lookup); this table turns that code into patient-facing copy — "tablet" / "by mouth"
 * instead of the clinical "Oral Tablet" / "Oral route" (#4580). The route phrase includes its
 * preposition ("by mouth", "in each eye") so it composes directly into a sig and the display
 * formatter doesn't have to guess one.
 *
 * Nasal/ophthalmic/otic dose forms derive a both-sides default ("in each nostril/eye/ear");
 * per-side variants ("in the left/right eye", …) are selectable in the form but never auto-derived
 * (a dose form carries no laterality), so they're appended to {@link PATIENT_FRIENDLY_ROUTES}
 * rather than stored against a code. Laterality is a body site, not a route, so all variants share
 * the same SNOMED route-of-administration code.
 *
 * Hand-curated copy, not raw RxNorm data — there is no open standard for these mappings. Keyed by
 * RxNorm DF rxcui; the DF name is noted per row for reference. The set of codes is pinned to a
 * checked-in RxNav snapshot (`__fixtures__/rxnormDoseForms.json`); `rxNormFormLookup.test.ts`
 * asserts this table covers exactly that snapshot, so a future RxNorm dose form can't slip in
 * unmapped.
 *
 * SNOMED route codes are retained where a clean concept exists, so the structured `dosage.route`
 * survives a round-trip into eRx flows. Routes with no clean SNOMED concept (unspecified injection,
 * implantation, irrigation) carry `undefined` and are stored as text only.
 */

import { SNOMED_SYSTEM } from './identifiers';

export interface RxNormFormEntry {
  /** Patient-friendly unit of the dose, e.g. "tablet", "mL", "puff". */
  unit: string;
  /** Patient-friendly route phrase including its preposition, e.g. "by mouth", "in each eye". */
  route: string;
  /** SNOMED CT route-of-administration code, when a clean concept exists. */
  snomedRouteCode?: string;
}

// SNOMED CT route-of-administration concepts, referenced by the table below.
const ROUTE = {
  ORAL: '26643006', // Oral route
  SUBLINGUAL: '37839007', // Sublingual route
  BUCCAL: '54471007', // Buccal route
  INHALATION: '447694001', // Respiratory tract route
  NASAL: '46713006', // Nasal route
  OPHTHALMIC: '54485002', // Ophthalmic route
  OTIC: '10547007', // Otic route
  TOPICAL: '6064005', // Topical route
  TRANSDERMAL: '45890007', // Transdermal route
  RECTAL: '37161004', // Rectal route
  VAGINAL: '16857009', // Vaginal route
  URETHRAL: '90584004', // Urethral route
  INTRAUTERINE: '372464004', // Intrauterine route
} as const;

/** Patient-friendly unit + route for each RxNorm DF code. */
export const RXNORM_FORM_LOOKUP: Record<string, RxNormFormEntry> = {
  '1649570': { unit: 'unit', route: 'by injection' }, // Auto-Injector
  '858080': { unit: 'film', route: 'in the cheek', snomedRouteCode: ROUTE.BUCCAL }, // Buccal Film
  '970789': { unit: 'tablet', route: 'in the cheek', snomedRouteCode: ROUTE.BUCCAL }, // Buccal Tablet
  '1649572': { unit: 'unit', route: 'by injection' }, // Cartridge
  '2269573': { unit: 'tablet', route: 'by mouth', snomedRouteCode: ROUTE.ORAL }, // Chewable Extended Release Oral Tablet
  '91058': { unit: 'tablet', route: 'by mouth', snomedRouteCode: ROUTE.ORAL }, // Chewable Tablet
  '402499': { unit: 'piece', route: 'by mouth', snomedRouteCode: ROUTE.ORAL }, // Chewing Gum
  '316995': { unit: 'capsule', route: 'by mouth', snomedRouteCode: ROUTE.ORAL }, // Delayed Release Oral Capsule
  '2284290': { unit: 'packet', route: 'by mouth', snomedRouteCode: ROUTE.ORAL }, // Delayed Release Oral Granules
  '10312': { unit: 'tablet', route: 'by mouth', snomedRouteCode: ROUTE.ORAL }, // Delayed Release Oral Tablet
  '316942': { unit: 'tablet', route: 'by mouth', snomedRouteCode: ROUTE.ORAL }, // Disintegrating Oral Tablet
  '479172': { unit: 'mL', route: 'vaginally', snomedRouteCode: ROUTE.VAGINAL }, // Douche
  '657710': { unit: 'unit', route: 'as an implant' }, // Drug Implant
  '744995': { unit: 'puff', route: 'by inhalation', snomedRouteCode: ROUTE.INHALATION }, // Dry Powder Inhaler
  '1535727': { unit: 'tablet', route: 'by mouth', snomedRouteCode: ROUTE.ORAL }, // Effervescent Oral Tablet
  '317678': { unit: 'mL', route: 'rectally', snomedRouteCode: ROUTE.RECTAL }, // Enema
  '316943': { unit: 'capsule', route: 'by mouth', snomedRouteCode: ROUTE.ORAL }, // Extended Release Oral Capsule
  '316945': { unit: 'tablet', route: 'by mouth', snomedRouteCode: ROUTE.ORAL }, // Extended Release Oral Tablet
  '316946': { unit: 'mL', route: 'by mouth', snomedRouteCode: ROUTE.ORAL }, // Extended Release Suspension
  '316999': { unit: 'dose', route: 'by inhalation', snomedRouteCode: ROUTE.INHALATION }, // Gas for Inhalation
  '1540453': { unit: 'packet', route: 'by mouth', snomedRouteCode: ROUTE.ORAL }, // Granules for Oral Solution
  '1540454': { unit: 'packet', route: 'by mouth', snomedRouteCode: ROUTE.ORAL }, // Granules for Oral Suspension
  '317000': { unit: 'puff', route: 'by inhalation', snomedRouteCode: ROUTE.INHALATION }, // Inhalation Powder
  '346161': { unit: 'mL', route: 'by inhalation', snomedRouteCode: ROUTE.INHALATION }, // Inhalation Solution
  '2284289': { unit: 'spray', route: 'by inhalation', snomedRouteCode: ROUTE.INHALATION }, // Inhalation Spray
  '2107948': { unit: 'puff', route: 'by inhalation', snomedRouteCode: ROUTE.INHALATION }, // Inhalation Suspension
  '1484929': { unit: 'mL', route: 'by injection' }, // Injectable Foam
  '316949': { unit: 'mL', route: 'by injection' }, // Injectable Solution
  '316950': { unit: 'mL', route: 'by injection' }, // Injectable Suspension
  '1649574': { unit: 'mL', route: 'by injection' }, // Injection
  '316951': { unit: 'mL', route: 'into the abdomen' }, // Intraperitoneal Solution
  '1732876': { unit: 'mL', route: 'into the airway' }, // Intratracheal Suspension
  '1856271': { unit: 'unit', route: 'into the uterus', snomedRouteCode: ROUTE.INTRAUTERINE }, // Intrauterine System
  '2719932': { unit: 'mL', route: 'into the bladder' }, // Intravesical Solution
  '2719933': { unit: 'mL', route: 'into the bladder' }, // Intravesical Suspension
  '152903': { unit: 'mL', route: 'as an irrigation' }, // Irrigation Solution
  '1649573': { unit: 'unit', route: 'by injection' }, // Jet Injector
  '316954': { unit: 'application', route: 'to the skin', snomedRouteCode: ROUTE.TOPICAL }, // Medicated Bar Soap
  '316955': { unit: 'application', route: 'to the skin', snomedRouteCode: ROUTE.TOPICAL }, // Medicated Liquid Soap
  '797271': { unit: 'pad', route: 'to the skin', snomedRouteCode: ROUTE.TOPICAL }, // Medicated Pad
  '1792831': { unit: 'patch', route: 'onto the skin', snomedRouteCode: ROUTE.TRANSDERMAL }, // Medicated Patch
  '106332': { unit: 'application', route: 'to the scalp', snomedRouteCode: ROUTE.TOPICAL }, // Medicated Shampoo
  '797685': { unit: 'application', route: 'to the skin', snomedRouteCode: ROUTE.TOPICAL }, // Medicated Tape
  '721654': { unit: 'puff', route: 'by inhalation', snomedRouteCode: ROUTE.INHALATION }, // Metered Dose Inhaler
  '1797831': { unit: 'spray', route: 'in each nostril', snomedRouteCode: ROUTE.NASAL }, // Metered Dose Nasal Spray
  '7067': { unit: 'mL', route: 'as a rinse', snomedRouteCode: ROUTE.ORAL }, // Mouthwash
  '346163': { unit: 'spray', route: 'to the affected area' }, // Mucosal Spray
  '316956': { unit: 'application', route: 'to the affected area' }, // Mucous Membrane Topical Solution
  '316960': { unit: 'application', route: 'in each nostril', snomedRouteCode: ROUTE.NASAL }, // Nasal Gel
  '316959': { unit: 'spray', route: 'in each nostril', snomedRouteCode: ROUTE.NASAL }, // Nasal Inhalant
  '316961': { unit: 'application', route: 'in each nostril', snomedRouteCode: ROUTE.NASAL }, // Nasal Ointment
  '1739329': { unit: 'spray', route: 'in each nostril', snomedRouteCode: ROUTE.NASAL }, // Nasal Powder
  '316962': { unit: 'spray', route: 'in each nostril', snomedRouteCode: ROUTE.NASAL }, // Nasal Solution
  '126542': { unit: 'spray', route: 'in each nostril', snomedRouteCode: ROUTE.NASAL }, // Nasal Spray
  '317544': { unit: 'application', route: 'in each eye', snomedRouteCode: ROUTE.OPHTHALMIC }, // Ophthalmic Cream
  '316963': { unit: 'application', route: 'in each eye', snomedRouteCode: ROUTE.OPHTHALMIC }, // Ophthalmic Gel
  '244286': { unit: 'mL', route: 'in each eye', snomedRouteCode: ROUTE.OPHTHALMIC }, // Ophthalmic Irrigation Solution
  '91344': { unit: 'application', route: 'in each eye', snomedRouteCode: ROUTE.OPHTHALMIC }, // Ophthalmic Ointment
  '7670': { unit: 'drop', route: 'in each eye', snomedRouteCode: ROUTE.OPHTHALMIC }, // Ophthalmic Solution
  '2637984': { unit: 'spray', route: 'in each eye', snomedRouteCode: ROUTE.OPHTHALMIC }, // Ophthalmic Spray
  '316964': { unit: 'drop', route: 'in each eye', snomedRouteCode: ROUTE.OPHTHALMIC }, // Ophthalmic Suspension
  '316965': { unit: 'capsule', route: 'by mouth', snomedRouteCode: ROUTE.ORAL }, // Oral Capsule
  '316966': { unit: 'application', route: 'by mouth', snomedRouteCode: ROUTE.ORAL }, // Oral Cream
  '2269575': { unit: 'film', route: 'by mouth', snomedRouteCode: ROUTE.ORAL }, // Oral Film
  '316947': { unit: 'flake', route: 'by mouth', snomedRouteCode: ROUTE.ORAL }, // Oral Flakes
  '346284': { unit: 'application', route: 'by mouth', snomedRouteCode: ROUTE.ORAL }, // Oral Foam
  '346169': { unit: 'application', route: 'by mouth', snomedRouteCode: ROUTE.ORAL }, // Oral Gel
  '317690': { unit: 'packet', route: 'by mouth', snomedRouteCode: ROUTE.ORAL }, // Oral Granules
  '316992': { unit: 'lozenge', route: 'by mouth', snomedRouteCode: ROUTE.ORAL }, // Oral Lozenge
  '346288': { unit: 'application', route: 'by mouth', snomedRouteCode: ROUTE.ORAL }, // Oral Ointment
  '346171': { unit: 'application', route: 'by mouth', snomedRouteCode: ROUTE.ORAL }, // Oral Paste
  '317691': { unit: 'pellet', route: 'by mouth', snomedRouteCode: ROUTE.ORAL }, // Oral Pellet
  '346289': { unit: 'packet', route: 'by mouth', snomedRouteCode: ROUTE.ORAL }, // Oral Powder
  '316968': { unit: 'mL', route: 'by mouth', snomedRouteCode: ROUTE.ORAL }, // Oral Solution
  '346164': { unit: 'spray', route: 'by mouth', snomedRouteCode: ROUTE.ORAL }, // Oral Spray
  '316969': { unit: 'mL', route: 'by mouth', snomedRouteCode: ROUTE.ORAL }, // Oral Suspension
  '317541': { unit: 'tablet', route: 'by mouth', snomedRouteCode: ROUTE.ORAL }, // Oral Tablet
  '316989': { unit: 'wafer', route: 'by mouth', snomedRouteCode: ROUTE.ORAL }, // Oral Wafer
  '2703097': { unit: 'application', route: 'in each ear', snomedRouteCode: ROUTE.OTIC }, // Otic Gel
  '316972': { unit: 'application', route: 'in each ear', snomedRouteCode: ROUTE.OTIC }, // Otic Ointment
  '316973': { unit: 'drop', route: 'in each ear', snomedRouteCode: ROUTE.OTIC }, // Otic Solution
  '316974': { unit: 'drop', route: 'in each ear', snomedRouteCode: ROUTE.OTIC }, // Otic Suspension
  '746839': { unit: 'pack', route: 'as directed' }, // Pack
  '402496': { unit: 'application', route: 'to the affected area' }, // Paste
  '1649571': { unit: 'unit', route: 'by injection' }, // Pen Injector
  '2718816': { unit: 'mL', route: 'into the bladder' }, // Powder for Intravesical Solution
  '2719931': { unit: 'mL', route: 'into the bladder' }, // Powder for Intravesical Suspension
  '1540452': { unit: 'spray', route: 'in each nostril', snomedRouteCode: ROUTE.NASAL }, // Powder for Nasal Solution
  '1540455': { unit: 'packet', route: 'by mouth', snomedRouteCode: ROUTE.ORAL }, // Powder for Oral Solution
  '1540456': { unit: 'packet', route: 'by mouth', snomedRouteCode: ROUTE.ORAL }, // Powder for Oral Suspension
  '2375729': { unit: 'mL', route: 'into the kidney' }, // Powder for Pyelocalyceal Solution
  '317004': { unit: 'spray', route: 'to the affected area' }, // Powder Spray
  '721656': { unit: 'mL', route: 'by injection' }, // Prefilled Syringe
  '316975': { unit: 'application', route: 'rectally', snomedRouteCode: ROUTE.RECTAL }, // Rectal Cream
  '317542': { unit: 'application', route: 'rectally', snomedRouteCode: ROUTE.RECTAL }, // Rectal Foam
  '346170': { unit: 'application', route: 'rectally', snomedRouteCode: ROUTE.RECTAL }, // Rectal Gel
  '317005': { unit: 'application', route: 'rectally', snomedRouteCode: ROUTE.RECTAL }, // Rectal Ointment
  '317006': { unit: 'mL', route: 'rectally', snomedRouteCode: ROUTE.RECTAL }, // Rectal Solution
  '316977': { unit: 'spray', route: 'rectally', snomedRouteCode: ROUTE.RECTAL }, // Rectal Spray
  '316978': { unit: 'suppository', route: 'rectally', snomedRouteCode: ROUTE.RECTAL }, // Rectal Suppository
  '2269578': { unit: 'film', route: 'under the tongue', snomedRouteCode: ROUTE.SUBLINGUAL }, // Sublingual Film
  '1810682': { unit: 'packet', route: 'under the tongue', snomedRouteCode: ROUTE.SUBLINGUAL }, // Sublingual Powder
  '317007': { unit: 'tablet', route: 'under the tongue', snomedRouteCode: ROUTE.SUBLINGUAL }, // Sublingual Tablet
  '316980': { unit: 'tablet', route: 'in the cheek', snomedRouteCode: ROUTE.BUCCAL }, // Sustained Release Buccal Tablet
  '1861409': { unit: 'tablet', route: 'by mouth', snomedRouteCode: ROUTE.ORAL }, // Tablet for Oral Suspension
  '10652': { unit: 'application', route: 'to the teeth', snomedRouteCode: ROUTE.ORAL }, // Toothpaste
  '316982': { unit: 'application', route: 'to the skin', snomedRouteCode: ROUTE.TOPICAL }, // Topical Cream
  '346285': { unit: 'application', route: 'to the skin', snomedRouteCode: ROUTE.TOPICAL }, // Topical Foam
  '346286': { unit: 'application', route: 'to the skin', snomedRouteCode: ROUTE.TOPICAL }, // Topical Gel
  '1788820': { unit: 'spray', route: 'to the skin', snomedRouteCode: ROUTE.TOPICAL }, // Topical Liquefied Gas
  '316983': { unit: 'application', route: 'to the skin', snomedRouteCode: ROUTE.TOPICAL }, // Topical Lotion
  '316984': { unit: 'application', route: 'to the skin', snomedRouteCode: ROUTE.TOPICAL }, // Topical Oil
  '316985': { unit: 'application', route: 'to the skin', snomedRouteCode: ROUTE.TOPICAL }, // Topical Ointment
  '317009': { unit: 'application', route: 'to the skin', snomedRouteCode: ROUTE.TOPICAL }, // Topical Powder
  '316986': { unit: 'application', route: 'to the skin', snomedRouteCode: ROUTE.TOPICAL }, // Topical Solution
  '346165': { unit: 'spray', route: 'to the skin', snomedRouteCode: ROUTE.TOPICAL }, // Topical Spray
  '1649353': { unit: 'application', route: 'to the skin', snomedRouteCode: ROUTE.TOPICAL }, // Topical Suspension
  '316987': { unit: 'patch', route: 'onto the skin', snomedRouteCode: ROUTE.TRANSDERMAL }, // Transdermal System
  '317543': { unit: 'suppository', route: 'into the urethra', snomedRouteCode: ROUTE.URETHRAL }, // Urethral Suppository
  '11103': { unit: 'application', route: 'vaginally', snomedRouteCode: ROUTE.VAGINAL }, // Vaginal Cream
  '1788819': { unit: 'film', route: 'vaginally', snomedRouteCode: ROUTE.VAGINAL }, // Vaginal Film
  '11107': { unit: 'dose', route: 'vaginally', snomedRouteCode: ROUTE.VAGINAL }, // Vaginal Foam
  '11108': { unit: 'application', route: 'vaginally', snomedRouteCode: ROUTE.VAGINAL }, // Vaginal Gel
  '2107950': { unit: 'insert', route: 'vaginally', snomedRouteCode: ROUTE.VAGINAL }, // Vaginal Insert
  '317010': { unit: 'application', route: 'vaginally', snomedRouteCode: ROUTE.VAGINAL }, // Vaginal Ointment
  '2199220': { unit: 'unit', route: 'vaginally', snomedRouteCode: ROUTE.VAGINAL }, // Vaginal System
};

/** The patient-friendly unit + route for an RxNorm DF code, or `undefined` if the code isn't a
 * known dose form. */
export function lookupByRxNormForm(code: string): RxNormFormEntry | undefined {
  return RXNORM_FORM_LOOKUP[code];
}

/**
 * Strength units that are also valid *dose* units, for drugs dosed by drug quantity rather than by
 * container.
 *
 * For a liquid — injectables especially — the correct sig states the **concentration-independent
 * unit**: the numerator of the strength, not the device holding it. Zepbound is `2.5 mg/0.5 mL`, so
 * its sig is "Inject 2.5 mg", not "1 pen" or "0.5 mL"; U-100 insulin is `100 unit/mL`, so its sig is
 * "Inject 10 units", not "0.1 mL". Dosing by container is how a concentration change silently becomes
 * a dosing error, which is why the drug quantity is the safe framing.
 *
 * These are not dose *forms* — no RxNorm DF maps to "mg" — so they are added to the option set
 * explicitly rather than derived from {@link RXNORM_FORM_LOOKUP}. `unit` already appears there (via the
 * injector dose forms) and so needs no entry here.
 */
const STRENGTH_DOSE_UNITS: readonly string[] = ['mcg', 'mg'];

/**
 * Container units for devices and supplies — meters, sensors, test strips, lancets, pen needles,
 * nebulizers, spacers, peak flow and blood-pressure monitors.
 *
 * These are dispensed by container, not by dose: a glucose meter ships as a kit, test strips and
 * lancets by the box. ScriptSure spells it the same way in the rare supply sig it does carry —
 * "1 Box - Take __ box per provider medication administration instruction" — a quantity and a
 * container noun, with the dose itself left blank.
 *
 * Kept distinct from {@link STRENGTH_DOSE_UNITS} because the reasoning is opposite: those exist so a
 * liquid is dosed by drug quantity rather than by its container, whereas a supply has no drug
 * quantity to state. Note `unit` is not one of these — it means an insulin/heparin unit, a real dose
 * measure, so applying it to a meter reads as a dose that was never prescribed.
 *
 * `kit` is selectable but never seeded automatically: a kit-named product (e.g. "FT BLOOD PRESSURE
 * MANUAL KIT") is a durable item, so `supplyUnit` seeds `device` and leaves `kit` for a prescriber
 * who wants the vendor's own word for it.
 */
const SUPPLY_CONTAINER_UNITS: readonly string[] = ['box', 'device', 'kit'];

/**
 * All units selectable in the form's "Units" field, sorted: every patient-friendly dose-form unit,
 * the {@link STRENGTH_DOSE_UNITS} that liquids are dosed in, and the
 * {@link SUPPLY_CONTAINER_UNITS} that devices and supplies are dispensed in.
 */
export const PATIENT_FRIENDLY_UNITS: string[] = [
  ...new Set([
    ...Object.values(RXNORM_FORM_LOOKUP).map((e) => e.unit),
    ...STRENGTH_DOSE_UNITS,
    ...SUPPLY_CONTAINER_UNITS,
  ]),
].sort((a, b) => a.localeCompare(b));

/** A selectable route option: the patient-friendly phrase plus its SNOMED code when one exists. */
export interface PatientFriendlyRoute {
  display: string;
  snomedCode?: string;
}

/** Per-side route variants for the eye/ear/nose. A dose form has no laterality, so these are never
 * auto-derived — they're offered alongside the both-sides default ("in each eye") so a prescriber
 * can specify one side. Laterality is a body site, not a route, so each variant keeps the same
 * SNOMED route-of-administration code as its "each" counterpart. The side therefore lives only in
 * the route's display text (and the composed sig), not in a structured `dosage.site` qualifier — so
 * left/right/each are indistinguishable to anything reading the coded route until `dosage.site` is
 * wired up. The free-text sig carries the side, which is where a paper script puts it. */
const LATERAL_ROUTES: PatientFriendlyRoute[] = [
  { display: 'in the left eye', snomedCode: ROUTE.OPHTHALMIC },
  { display: 'in the right eye', snomedCode: ROUTE.OPHTHALMIC },
  { display: 'in the left ear', snomedCode: ROUTE.OTIC },
  { display: 'in the right ear', snomedCode: ROUTE.OTIC },
  { display: 'in the left nostril', snomedCode: ROUTE.NASAL },
  { display: 'in the right nostril', snomedCode: ROUTE.NASAL },
];

/** All distinct patient-friendly routes, deduped by display phrase and sorted — the option set for
 * the form's "Route" field. Combines the dose-form-derived routes with the per-side {@link
 * LATERAL_ROUTES} variants. Throws if one display phrase carries two different SNOMED codes:
 * dedup would otherwise silently pick one by insertion order, corrupting the option's code.
 * (Keeping a phrase ↔ code 1:1 is an invariant the option set must hold.) */
export const PATIENT_FRIENDLY_ROUTES: PatientFriendlyRoute[] = (() => {
  const byDisplay = new Map<string, PatientFriendlyRoute>();
  const add = (display: string, snomedCode: string | undefined): void => {
    const existing = byDisplay.get(display);
    if (existing && existing.snomedCode !== snomedCode) {
      throw new Error(
        `PATIENT_FRIENDLY_ROUTES: route "${display}" maps to conflicting SNOMED codes (${existing.snomedCode} vs ${snomedCode})`,
      );
    }
    if (!existing) byDisplay.set(display, { display, snomedCode });
  };
  for (const { route, snomedRouteCode } of Object.values(RXNORM_FORM_LOOKUP)) add(route, snomedRouteCode);
  for (const { display, snomedCode } of LATERAL_ROUTES) add(display, snomedCode);
  return [...byDisplay.values()].sort((a, b) => a.display.localeCompare(b.display));
})();

/** Build the `doseMethod`/`dosage.route` CodeableConcept input from a patient-friendly route display
 * (e.g. "by mouth"): the display becomes `text`, and the route's SNOMED code (when the table has one)
 * becomes a `coding`. Lets the medication form submit just the display string while the structured
 * route survives for eRx round-trips. Returns text-only for routes with no SNOMED concept. */
export function medicationRouteToCodeableConcept(routeDisplay: string): {
  text: string;
  coding?: { system: string; code: string; display: string }[];
} {
  const match = PATIENT_FRIENDLY_ROUTES.find((r) => r.display === routeDisplay);
  return match?.snomedCode
    ? { text: routeDisplay, coding: [{ system: SNOMED_SYSTEM, code: match.snomedCode, display: routeDisplay }] }
    : { text: routeDisplay };
}
